import {
  Calendar,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  GitMerge,
  List,
  Rocket,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useReleaseTimeline } from '../hooks/useReleaseHistory.js';
import { ReleaseCalendarView } from '../components/ReleaseCalendarView.js';
import { backlogUrl } from '../lib/backlog-url.js';
import type { ReleaseEntry, UxImpact } from '../types/release.js';

/** @graph-connects none */
type ViewTab = 'timeline' | 'calendar';

/** UX影響度バッジの配色（重い順に強い色）。 @graph-connects none */
const UX_TINT: Record<UxImpact, string> = {
  ux_change: 'var(--color-accent-primary)',
  indirect: 'var(--color-text-secondary)',
  none: 'var(--color-text-muted)',
};

/** mergedAt(ISO) を JST の YYYY/MM/DD に整形する。 @graph-connects none */
function toJstDate(iso: string): string {
  const formatted = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
  return formatted.replace(/-/g, '/');
}

/**
 * curatedAt(ISO) を JST の「YYYY/MM/DD HH:mm」に整形する。
 * ロケール差で区切り・ゼロ詰め・24時表記が揺れないよう formatToParts から組み立てる。
 * @graph-connects none
 */
function toJstDateTime(iso: string): string {
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}/${get('month')}/${get('day')} ${get('hour')}:${get('minute')}`;
}

interface DateGroup {
  date: string;
  entries: ReleaseEntry[];
}

/** エントリ（mergedAt 降順前提）を JST 日付ごとにまとめる。 @graph-connects none */
function groupByDate(entries: ReleaseEntry[]): DateGroup[] {
  const groups: DateGroup[] = [];
  for (const entry of entries) {
    const date = toJstDate(entry.mergedAt);
    const last = groups.length > 0 ? groups[groups.length - 1] : undefined;
    if (last && last.date === date) {
      last.entries.push(entry);
    } else {
      groups.push({ date, entries: [entry] });
    }
  }
  return groups;
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business リリース履歴ページ。メンズ main マージPRの発信トーン要約をタイムライン（新しい順）とカレンダー（月グリッド）の2ビューで表示し、カテゴリ絞り込み・UX影響バッジ・Backlog/PRリンクを提供する。データ未投入時は準備中を表示。
 * @graph-connects useReleaseTimeline [calls] リリース履歴タイムライン取得フック
 * @graph-connects ReleaseCalendarView [contains] カレンダービューコンポーネント
 */
export function ReleaseHistoryPage() {
  const { t } = useTranslation();
  const [category, setCategory] = useState('');
  const [uxImpact, setUxImpact] = useState('');
  const [viewTab, setViewTab] = useState<ViewTab>('timeline');
  const [expandedPrs, setExpandedPrs] = useState<Set<number>>(new Set());
  const query = useReleaseTimeline(category || undefined, uxImpact || undefined);

  const toggleExpand = useCallback((prNumber: number) => {
    setExpandedPrs((prev) => {
      const next = new Set(prev);
      if (next.has(prNumber)) {
        next.delete(prNumber);
      } else {
        next.add(prNumber);
      }
      return next;
    });
  }, []);

  const entries = query.data?.entries ?? [];
  const categories = query.data?.categories ?? [];
  const uxImpacts = query.data?.uxImpacts ?? [];
  const categoryLabel = useMemo(
    () => new Map(categories.map((c) => [c.key, c.label])),
    [categories],
  );
  const uxLabel = useMemo(() => new Map(uxImpacts.map((u) => [u.key, u.label])), [uxImpacts]);
  const groups = useMemo(() => groupByDate(entries), [entries]);

  return (
    <div className="mx-auto w-full max-w-[900px] px-4 py-8">
      <div className="mb-1 font-mono text-xs tracking-widest text-[var(--color-accent-primary)] uppercase">
        Product
      </div>
      <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
        {t('releaseHistory.title')}
      </h1>
      <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
        {t('releaseHistory.description')}
      </p>
      {query.data?.lastUpdatedAt ? (
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
          {t('common.lastUpdated')}: {toJstDateTime(query.data.lastUpdatedAt)}
        </p>
      ) : null}

      <Card className="mt-6 flex flex-wrap items-end gap-4 p-4">
        <label className="flex flex-col gap-1 text-xs text-[var(--color-text-secondary)]">
          {t('releaseHistory.category')}
          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
            }}
            className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)]"
          >
            <option value="">{t('common.all')}</option>
            {categories.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-[var(--color-text-secondary)]">
          {t('releaseHistory.uxImpact')}
          <select
            value={uxImpact}
            onChange={(e) => {
              setUxImpact(e.target.value);
            }}
            className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)]"
          >
            <option value="">{t('common.all')}</option>
            {uxImpacts.map((u) => (
              <option key={u.key} value={u.key}>
                {u.label}
              </option>
            ))}
          </select>
        </label>
      </Card>

      <div className="mt-4 flex gap-1" role="tablist" aria-label={t('releaseHistory.viewSwitch')}>
        <button
          type="button"
          role="tab"
          aria-selected={viewTab === 'timeline'}
          onClick={() => {
            setViewTab('timeline');
          }}
          className={[
            'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
            viewTab === 'timeline'
              ? 'bg-[var(--color-accent-primary)] text-white'
              : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-base)]',
          ].join(' ')}
        >
          <List className="h-4 w-4" aria-hidden="true" />
          {t('releaseHistory.timeline')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={viewTab === 'calendar'}
          onClick={() => {
            setViewTab('calendar');
          }}
          className={[
            'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
            viewTab === 'calendar'
              ? 'bg-[var(--color-accent-primary)] text-white'
              : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-base)]',
          ].join(' ')}
        >
          <Calendar className="h-4 w-4" aria-hidden="true" />
          {t('releaseHistory.calendar')}
        </button>
      </div>

      {query.isLoading ? (
        <p className="mt-8 text-sm text-[var(--color-text-muted)]">{t('app.loading')}</p>
      ) : query.isError ? (
        <Card className="mt-6 p-6">
          <p className="text-sm text-[var(--color-text-secondary)]">
            {t('releaseHistory.fetchError')}
          </p>
        </Card>
      ) : entries.length === 0 ? (
        <Card className="mt-6 p-8 text-center">
          <Rocket className="mx-auto h-8 w-8 text-[var(--color-text-muted)]" aria-hidden="true" />
          <p className="mt-3 text-sm text-[var(--color-text-secondary)]">
            {t('releaseHistory.noData')}
          </p>
        </Card>
      ) : viewTab === 'calendar' ? (
        <ReleaseCalendarView entries={entries} categoryLabel={categoryLabel} uxLabel={uxLabel} />
      ) : (
        <div className="mt-8">
          {groups.map((group) => (
            <section key={group.date} className="mb-8">
              <div className="mb-3 flex items-center gap-3">
                <span className="font-mono text-sm font-semibold text-[var(--color-text-primary)]">
                  {group.date}
                </span>
                <span className="h-px flex-1 bg-[var(--color-border)]" />
                <span className="text-xs text-[var(--color-text-muted)]">
                  {t('release.entryCount', { num: String(group.entries.length) })}
                </span>
              </div>
              <ul className="flex flex-col gap-3">
                {group.entries.map((entry) => {
                  const isExpanded = expandedPrs.has(entry.prNumber);
                  return (
                    <li key={entry.prNumber}>
                      <Card
                        className="cursor-pointer p-4 transition-shadow hover:shadow-md"
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          toggleExpand(entry.prNumber);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleExpand(entry.prNumber);
                          }
                        }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span
                              className="rounded-full px-2 py-0.5 text-xs font-medium"
                              style={{
                                color: UX_TINT[entry.uxImpact],
                                border: `1px solid ${UX_TINT[entry.uxImpact]}`,
                              }}
                            >
                              {uxLabel.get(entry.uxImpact) ?? entry.uxImpact}
                            </span>
                            <span className="rounded-full bg-[var(--color-bg-base)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)]">
                              {categoryLabel.get(entry.category) ?? entry.category}
                            </span>
                          </div>
                          {isExpanded ? (
                            <ChevronUp
                              className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]"
                              aria-hidden="true"
                            />
                          ) : (
                            <ChevronDown
                              className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]"
                              aria-hidden="true"
                            />
                          )}
                        </div>
                        <p
                          className={[
                            'mt-2 text-base font-semibold text-[var(--color-text-primary)]',
                            isExpanded ? '' : 'line-clamp-2',
                          ].join(' ')}
                        >
                          {entry.summary}
                        </p>
                        {isExpanded ? (
                          <div className="mt-3 rounded-lg bg-[var(--color-bg-base)] p-3">
                            <p className="text-sm text-[var(--color-text-secondary)]">
                              {entry.title}
                            </p>
                          </div>
                        ) : null}
                        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-[var(--color-text-muted)]">
                          <a
                            href={entry.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 hover:text-[var(--color-accent-primary)]"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <GitMerge className="h-3.5 w-3.5" aria-hidden="true" />#{entry.prNumber}
                          </a>
                          {entry.backlogKey ? (
                            <a
                              href={backlogUrl(entry.backlogKey)}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 hover:text-[var(--color-accent-primary)]"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                              {entry.backlogKey}
                            </a>
                          ) : null}
                          <span>{entry.author}</span>
                        </div>
                      </Card>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">
            {t('release.entryCount', { num: entries.length.toLocaleString('ja-JP') })}
          </p>
        </div>
      )}
    </div>
  );
}
